'use client';

import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Code } from '@/components/ui/code';
import { LocaleLink } from '@/components/locale-link';

const PACKAGES = [
  {
    id: 'python',
    name: 'firmendata',
    repository: 'https://github.com/FirmenData/firmendata-python',
    registry: 'https://pypi.org/project/firmendata/',
    badge: 'https://img.shields.io/pypi/v/firmendata',
    install: 'pip install firmendata',
    keyless: `from firmendata import FirmenData

# No API key needed for autocomplete
for hit in FirmenData().autocomplete("siemens")["data"]:
    print(hit["eu_id"], hit["display_name"])`,
    authenticated: `from firmendata import FirmenData

fd = FirmenData(api_key="firmendata_live_...")
company = fd.get_company("DED2601V.HRB6684")`,
  },
  {
    id: 'javascript',
    name: 'firmendata',
    repository: 'https://github.com/FirmenData/firmendata-node',
    registry: 'https://www.npmjs.com/package/firmendata',
    badge: 'https://img.shields.io/npm/v/firmendata',
    install: 'npm install firmendata',
    keyless: `import { FirmenData } from 'firmendata';

// No API key needed for autocomplete
const { data } = await new FirmenData().autocomplete('siemens');`,
    authenticated: `import { FirmenData } from 'firmendata';

const fd = new FirmenData({ apiKey: process.env.FIRMENDATA_API_KEY });
const company = await fd.getCompany('DED2601V.HRB6684');`,
  },
  {
    id: 'n8n',
    name: 'n8n-nodes-firmendata',
    repository: 'https://github.com/FirmenData/n8n-nodes-firmendata',
    registry: 'https://www.npmjs.com/package/n8n-nodes-firmendata',
    badge: 'https://img.shields.io/npm/v/n8n-nodes-firmendata',
    install: 'n8n-nodes-firmendata',
  },
] as const;

const OPERATIONS = [
  'autocomplete',
  'search',
  'get',
  'financials',
  'shareholders',
  'ubo',
  'history',
  'listDocuments',
  'downloadDocument',
] as const;

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="mb-4 text-2xl font-semibold tracking-tight">{title}</h2>
      <div className="space-y-4 text-base leading-relaxed text-muted-foreground">
        {children}
      </div>
    </section>
  );
}

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="max-w-full overflow-x-auto rounded-lg border border-border bg-muted p-4 text-sm leading-relaxed text-foreground">
      <code>{children}</code>
    </pre>
  );
}

export function SdksContent() {
  const { t } = useTranslation();
  const strongInline = <strong className="text-foreground" />;
  const codeInline = (
    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-sm text-foreground [overflow-wrap:anywhere]" />
  );
  const linkClass = 'font-medium text-primary underline underline-offset-4';

  return (
    <div className="mx-auto w-full min-w-0 max-w-4xl space-y-12 px-4 py-12 lg:py-16">
      <header className="border-b border-border pb-10">
        <Badge variant="secondary" className="mb-4">
          {t('sdks.hero.badge')}
        </Badge>
        <h1 className="text-4xl font-bold tracking-tight lg:text-5xl">
          {t('sdks.hero.title')}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground lg:text-xl">
          {t('sdks.hero.subtitle')}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button asChild size="lg">
            <LocaleLink href="/developer/apis">
              {t('sdks.hero.reference')}
            </LocaleLink>
          </Button>
          <Button asChild size="lg" variant="outline">
            <LocaleLink href="/account/api-keys">
              {t('sdks.hero.apiKey')}
            </LocaleLink>
          </Button>
        </div>
      </header>

      <Card className="border-primary/40 bg-primary/5">
        <CardHeader>
          <CardTitle as="h2" className="py-4 text-xl">
            {t('sdks.autocomplete.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 leading-relaxed text-muted-foreground">
          <p>
            <Trans
              i18nKey="sdks.autocomplete.body"
              components={{ strong: strongInline, code: codeInline }}
            />
          </p>
          <p>{t('sdks.autocomplete.limits')}</p>
          <p>
            <Trans
              i18nKey="sdks.autocomplete.retry"
              components={{ code: codeInline }}
            />
          </p>
          <CodeBlock>
            {
              'curl "https://api.firmendata.com/v1/companies/autocomplete?q=siemens"'
            }
          </CodeBlock>
        </CardContent>
      </Card>

      <Section id="packages" title={t('sdks.packages.title')}>
        {PACKAGES.map((pkg) => (
          <Card key={pkg.id} className="min-w-0">
            <CardHeader className="gap-3 py-4">
              <div className="min-w-0 space-y-2">
                <CardTitle className="text-xl text-foreground">
                  {t(`sdks.packages.${pkg.id}.title`)}
                </CardTitle>
                <code className="break-all text-sm">{pkg.name}</code>
              </div>
              <a
                href={pkg.registry}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-10 items-center"
              >
                <img
                  src={pkg.badge}
                  alt={t('sdks.packages.versionAlt', { package: pkg.name })}
                  loading="lazy"
                />
              </a>
            </CardHeader>
            <CardContent className="space-y-5">
              <p>{t(`sdks.packages.${pkg.id}.description`)}</p>
              <div className="space-y-2">
                <h3 className="font-semibold text-foreground">
                  {t('sdks.packages.install')}
                </h3>
                {pkg.id === 'n8n' && (
                  <p>
                    <Trans
                      i18nKey="sdks.packages.n8n.installSteps"
                      components={{ code: codeInline }}
                    />
                  </p>
                )}
                <Code
                  asChild
                  showCopyButton
                  copyText={pkg.install}
                  copyLabel={t('sdks.packages.copyInstall')}
                  className="w-full rounded-lg border border-border bg-muted p-2 [&_button]:size-10 [&_button]:shrink-0 [&_[data-slot=code-panel]]:min-w-0 [&_[data-slot=code-panel]]:flex-1 [&_[data-slot=code-panel]]:overflow-x-auto"
                >
                  <pre>
                    <code>{pkg.install}</code>
                  </pre>
                </Code>
                {pkg.id === 'n8n' && (
                  <Button asChild size="lg" variant="outline">
                    <a
                      href="https://docs.n8n.io/integrations/community-nodes/installation/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('sdks.packages.n8n.installGuide')}
                    </a>
                  </Button>
                )}
              </div>

              {pkg.id === 'n8n' ? (
                <>
                  <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-4">
                    <h3 className="font-semibold text-foreground">
                      {t('sdks.packages.n8n.credentialsTitle')}
                    </h3>
                    <p>
                      <Trans
                        i18nKey="sdks.packages.n8n.credentials"
                        components={{
                          strong: strongInline,
                          code: codeInline,
                          apiKeyLink: (
                            <LocaleLink
                              href="/account/api-keys"
                              className={linkClass}
                            />
                          ),
                        }}
                      />
                    </p>
                  </div>
                  <div>
                    <h3 className="mb-2 font-semibold text-foreground">
                      {t('sdks.packages.n8n.operationsTitle')}
                    </h3>
                    <ul className="grid list-inside list-disc gap-x-6 gap-y-1 lg:grid-cols-2">
                      {OPERATIONS.map((operation) => (
                        <li key={operation}>
                          {t(`sdks.packages.n8n.operations.${operation}`)}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <h3 className="font-semibold text-foreground">
                      {t('sdks.packages.keyless')}
                    </h3>
                    <CodeBlock>{pkg.keyless}</CodeBlock>
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-semibold text-foreground">
                      {t('sdks.packages.withKey')}
                    </h3>
                    <CodeBlock>{pkg.authenticated}</CodeBlock>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <h3 className="font-semibold text-foreground">
                  {t('sdks.packages.requirements')}
                </h3>
                <p>
                  <Trans
                    i18nKey={`sdks.packages.${pkg.id}.requirements`}
                    components={{ code: codeInline }}
                  />
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button asChild size="lg" variant="outline">
                  <a
                    href={pkg.repository}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('sdks.packages.github')}
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a
                    href={pkg.registry}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t(`sdks.packages.${pkg.id}.registry`)}
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </Section>

      <Section id="releases" title={t('sdks.releases.title')}>
        <p>{t('sdks.releases.body')}</p>
        <ul className="flex flex-wrap gap-x-6 gap-y-2">
          {PACKAGES.map((pkg) => (
            <li key={pkg.id}>
              <a
                href={`${pkg.repository}/blob/main/CHANGELOG.md`}
                target="_blank"
                rel="noopener noreferrer"
                className={`${linkClass} inline-flex min-h-10 items-center`}
              >
                {t('sdks.releases.releaseLink', {
                  package: t(`sdks.packages.${pkg.id}.title`),
                })}
              </a>
            </li>
          ))}
        </ul>
        <p>
          <Trans
            i18nKey="sdks.releases.otherLanguages"
            components={{
              openapiLink: <a href="/api/openapi.json" className={linkClass} />,
              mcpLink: <LocaleLink href="/mcp" className={linkClass} />,
              code: codeInline,
            }}
          />
        </p>
      </Section>
    </div>
  );
}
